import { z } from "zod";
import { IdSchema, Sha256Schema } from "./primitives.js";
/**
 * "+" manual orders: a physician picks a clinic form in the encounter and orders from it, apart
 * from the visit plan. Each one carries a marker (its own id and the exact form it was picked on)
 * and runs under its own idempotency key, `manual-order:<id>:<attempt>`. The key, stored on the
 * agent run, is the order's lineage: the database tells "+" runs from plan runs by it alone.
 *
 * Leaf module: it imports only primitives, so orders, agent and clinic-document contracts can all
 * import it without an import cycle.
 */
/**
 * A "+" order's id, minted by the browser. Lower-case only, because the database recognises a
 * "+" run key by a lower-case pattern: an upper-case id would make a "+" run look like a plan run.
 */
export const ManualOrderIdSchema = IdSchema.regex(/^[0-9a-f-]+$/u, "A manual order id must be a lower-case UUID");
/**
 * The exact published form a "+" order was picked on. The same shape as
 * ImagingFacilityFormReferenceSchema (orders.ts), declared here so this module stays a leaf.
 */
export const ManualOrderFormRefSchema = z
    .object({
    documentId: IdSchema,
    documentVersion: z.number().int().positive(),
    documentVersionHash: Sha256Schema,
})
    .strict();
/** True when both refer to exactly the same published form version. */
export function sameManualOrderForm(left, right) {
    return (left.documentId === right.documentId &&
        left.documentVersion === right.documentVersion &&
        left.documentVersionHash === right.documentVersionHash);
}
/** Carried by a "+" order's proposal. Its key must be manualOrderRunKey(id, attempt). */
export const ManualOrderMarkerSchema = z
    .object({
    id: ManualOrderIdSchema,
    form: ManualOrderFormRefSchema,
})
    .strict();
/** Reserved for "+" runs: no other caller may start an idempotency key with it. */
export const MANUAL_ORDER_RUN_KEY_PREFIX = "manual-order:";
/** The highest attempt a run key can carry (six digits, as the database pattern allows). */
export const MANUAL_ORDER_MAX_ATTEMPT = 999_999;
/**
 * Exactly the database's pattern for a "+" run key: the order id, then the attempt from 1. Any
 * other key, including a malformed one with the reserved prefix, is not a "+" run.
 */
export const MANUAL_ORDER_RUN_KEY_PATTERN = /^manual-order:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):([1-9][0-9]{0,5})$/u;
/**
 * The idempotency key of one attempt at preparing a "+" order. A new attempt (after the ticks or
 * the chart changed) gets a new key, so retries of the same attempt replay instead of re-ordering.
 */
export function manualOrderRunKey(id, attempt) {
    if (!ManualOrderIdSchema.safeParse(id).success)
        throw new RangeError("A manual order id must be a lower-case UUID");
    if (!Number.isInteger(attempt) || attempt < 1 || attempt > MANUAL_ORDER_MAX_ATTEMPT)
        throw new RangeError(`A manual order attempt must be a whole number from 1 to ${MANUAL_ORDER_MAX_ATTEMPT}`);
    return `${MANUAL_ORDER_RUN_KEY_PREFIX}${id}:${attempt}`;
}
/**
 * The order id and attempt of a "+" run key, or null for any other key. It reads keys exactly as
 * the database does, so callers still compare the id with the proposal's marker.
 */
export function parseManualOrderRunKey(key) {
    const match = MANUAL_ORDER_RUN_KEY_PATTERN.exec(key);
    const id = match?.[1];
    const attempt = match?.[2];
    return id === undefined || attempt === undefined ? null : { id, attempt: Number(attempt) };
}
/**
 * Why a run's idempotency key and its "+" marker do not belong together, or null when they do. A
 * marked proposal runs only under its own key, `manualOrderRunKey(marker.id, attempt)` for some
 * attempt, and the reserved prefix is never used without the marker it names. So a plan run can
 * never pass for a "+" run, nor a "+" run for a plan run or for another "+" order.
 */
export function manualOrderKeyMismatch(marker, idempotencyKey) {
    if (marker === undefined) {
        return idempotencyKey.startsWith(MANUAL_ORDER_RUN_KEY_PREFIX)
            ? "A manual-order key is used only by the manual order it names"
            : null;
    }
    return parseManualOrderRunKey(idempotencyKey)?.id === marker.id
        ? null
        : "A manual order runs under its own key, manual-order:<id>:<attempt>";
}
/** Adds the key and marker rule to a request schema whose key is `idempotencyKey`. */
export function refineManualOrderRunKey(marker, idempotencyKey, context) {
    const mismatch = manualOrderKeyMismatch(marker, idempotencyKey);
    if (mismatch !== null) {
        context.addIssue({ code: "custom", path: ["idempotencyKey"], message: mismatch });
    }
}
/** The selection reason recorded on the form a physician picked for a "+" order. */
export const MANUAL_ORDER_FORM_REASON = "physician_selected_form";
//# sourceMappingURL=manual-order-marker.js.map